import { useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { getCountryName } from '@/lib/translations';

// Country slug mapping for canonical URLs
const COUNTRY_SLUGS = {
  'AT': 'austria', 'BE': 'belgium', 'BG': 'bulgaria', 'HR': 'croatia',
  'CY': 'cyprus', 'CZ': 'czech-republic', 'DK': 'denmark', 'EE': 'estonia',
  'FI': 'finland', 'FR': 'france', 'DE': 'germany', 'GR': 'greece',
  'HU': 'hungary', 'IE': 'ireland', 'IT': 'italy', 'LV': 'latvia',
  'LT': 'lithuania', 'LU': 'luxembourg', 'MT': 'malta', 'NL': 'netherlands',
  'PL': 'poland', 'PT': 'portugal', 'RO': 'romania', 'SK': 'slovakia',
  'SI': 'slovenia', 'ES': 'spain', 'SE': 'sweden',
  'GB': 'united-kingdom', 'CH': 'switzerland', 'NO': 'norway', 'IS': 'iceland',
  'RS': 'serbia', 'BA': 'bosnia', 'AL': 'albania', 'MK': 'north-macedonia',
  'ME': 'montenegro', 'UA': 'ukraine', 'TR': 'turkey',
  'AU': 'australia', 'NZ': 'new-zealand', 'CA': 'canada', 'IN': 'india',
  'JP': 'japan', 'KR': 'south-korea', 'SG': 'singapore', 'MY': 'malaysia',
  'TH': 'thailand', 'PH': 'philippines', 'ID': 'indonesia', 'VN': 'vietnam',
  'ZA': 'south-africa', 'BR': 'brazil', 'MX': 'mexico', 'AR': 'argentina',
  'CL': 'chile', 'CO': 'colombia', 'AE': 'uae', 'SA': 'saudi-arabia',
  'IL': 'israel', 'EG': 'egypt', 'NG': 'nigeria', 'KE': 'kenya'
};

// Country-specific FAQ generator for SEO - uses localized country names
const getCountryFAQs = (country, lang, localizedName) => {
  if (!country) return [];
  
  // Handle both vat_rate and standard_rate
  const vatRate = country.vat_rate || country.standard_rate || 0;
  if (!vatRate) return [];
  
  const vatRateStr = vatRate.toString().replace('.', ','); // European format
  const multiplier = (1 + vatRate / 100).toFixed(2).replace('.', ',');
  const name = localizedName || country.name;
  
  const faqTemplates = {
    en: [
      { q: `What is the VAT rate in ${name}?`, a: `The standard VAT rate in ${name} is ${vatRate}%. ${country.reduced_rates?.length > 0 ? `Reduced rates are: ${country.reduced_rates.join('%, ')}%.` : ''} The local name for VAT is ${country.tax_name}.` },
      { q: `How do I calculate VAT in ${name}?`, a: `To add VAT in ${name}, multiply your net amount by ${multiplier}. To remove VAT, divide the gross amount by ${multiplier}. Our calculator does this automatically.` },
      { q: `Do I need to register for VAT in ${name}?`, a: `VAT registration requirements in ${name} vary based on your business type and turnover. Generally, businesses exceeding the registration threshold must register. Consult the local tax authority for specific requirements.` },
      { q: `What goods and services are VAT exempt in ${name}?`, a: `Common VAT exemptions in ${name} typically include medical services, education, financial services, and insurance. Reduced rates may apply to food, books, and public transport.` }
    ],
    de: [
      { q: `Wie hoch ist der MwSt-Satz in ${name}?`, a: `Der Standard-MwSt-Satz in ${name} beträgt ${vatRate}%. ${country.reduced_rates?.length > 0 ? `Ermäßigte Sätze sind: ${country.reduced_rates.join('%, ')}%.` : ''} Der lokale Name für MwSt ist ${country.tax_name}.` },
      { q: `Wie berechne ich die MwSt in ${name}?`, a: `Um MwSt in ${name} hinzuzufügen, multiplizieren Sie den Nettobetrag mit ${multiplier}. Um MwSt herauszurechnen, teilen Sie den Bruttobetrag durch ${multiplier}. Unser Rechner macht das automatisch.` },
      { q: `Muss ich mich für MwSt in ${name} registrieren?`, a: `Die MwSt-Registrierungspflicht in ${name} hängt von Ihrer Geschäftsart und Ihrem Umsatz ab. Konsultieren Sie die lokale Steuerbehörde für spezifische Anforderungen.` },
      { q: `Welche Waren und Dienstleistungen sind in ${name} von der MwSt befreit?`, a: `Typische MwSt-Befreiungen in ${name} umfassen medizinische Dienste, Bildung, Finanzdienstleistungen und Versicherungen. Ermäßigte Sätze können für Lebensmittel, Bücher und öffentlichen Verkehr gelten.` }
    ],
    fr: [
      { q: `Quel est le taux de TVA en ${name}?`, a: `Le taux de TVA standard en ${name} est de ${vatRate}%. ${country.reduced_rates?.length > 0 ? `Les taux réduits sont: ${country.reduced_rates.join('%, ')}%.` : ''} Le nom local de la TVA est ${country.tax_name}.` },
      { q: `Comment calculer la TVA en ${name}?`, a: `Pour ajouter la TVA en ${name}, multipliez le montant HT par ${multiplier}. Pour retirer la TVA, divisez le montant TTC par ${multiplier}. Notre calculateur le fait automatiquement.` },
      { q: `Dois-je m'enregistrer à la TVA en ${name}?`, a: `Les exigences d'enregistrement à la TVA en ${name} varient selon votre type d'entreprise et votre chiffre d'affaires. Consultez l'autorité fiscale locale pour les exigences spécifiques.` },
      { q: `Quels biens et services sont exonérés de TVA en ${name}?`, a: `Les exonérations de TVA courantes en ${name} incluent généralement les services médicaux, l'éducation, les services financiers et les assurances. Des taux réduits peuvent s'appliquer à l'alimentation, aux livres et aux transports publics.` }
    ],
    it: [
      { q: `Qual è l'aliquota IVA in ${name}?`, a: `L'aliquota IVA standard in ${name} è del ${vatRate}%. ${country.reduced_rates?.length > 0 ? `Le aliquote ridotte sono: ${country.reduced_rates.join('%, ')}%.` : ''} Il nome locale dell'IVA è ${country.tax_name}.` },
      { q: `Come calcolare l'IVA in ${name}?`, a: `Per aggiungere l'IVA in ${name}, moltiplica l'importo netto per ${multiplier}. Per scorporare l'IVA, dividi l'importo lordo per ${multiplier}. Il nostro calcolatore lo fa automaticamente.` },
      { q: `Devo registrarmi per l'IVA in ${name}?`, a: `I requisiti di registrazione IVA in ${name} variano in base al tipo di attività e al fatturato. Consulta l'autorità fiscale locale per requisiti specifici.` },
      { q: `Quali beni e servizi sono esenti da IVA in ${name}?`, a: `Le esenzioni IVA comuni in ${name} includono tipicamente servizi medici, istruzione, servizi finanziari e assicurazioni. Aliquote ridotte possono applicarsi ad alimenti, libri e trasporti pubblici.` }
    ],
    es: [
      { q: `¿Cuál es el tipo de IVA en ${name}?`, a: `El tipo de IVA estándar en ${name} es del ${vatRate}%. ${country.reduced_rates?.length > 0 ? `Los tipos reducidos son: ${country.reduced_rates.join('%, ')}%.` : ''} El nombre local del IVA es ${country.tax_name}.` },
      { q: `¿Cómo calcular el IVA en ${name}?`, a: `Para añadir IVA en ${name}, multiplica el importe neto por ${multiplier}. Para quitar el IVA, divide el importe bruto por ${multiplier}. Nuestra calculadora lo hace automáticamente.` },
      { q: `¿Necesito registrarme para el IVA en ${name}?`, a: `Los requisitos de registro de IVA en ${name} varían según el tipo de negocio y la facturación. Consulte la autoridad fiscal local para requisitos específicos.` },
      { q: `¿Qué bienes y servicios están exentos de IVA en ${name}?`, a: `Las exenciones de IVA comunes en ${name} generalmente incluyen servicios médicos, educación, servicios financieros y seguros. Tipos reducidos pueden aplicarse a alimentos, libros y transporte público.` }
    ]
  };
  
  return faqTemplates[lang] || faqTemplates.en;
};

/**
 * SEO Component - Manages all meta tags, Schema.org markup, and Open Graph
 * Optimized for international SEO with proper hreflang and country-specific pages
 * KILLER SEO for Google, Bing, and AI crawlers
 */
const SEOHead = ({ page = 'home', country = null, locale = null }) => {
  const { t, language } = useLanguage();
  
  // Production domain
  const PRODUCTION_DOMAIN = 'https://vat.expert';

  useEffect(() => {
    // Don't override SEO tags on blog pages - they have their own SEO management
    if (window.location.pathname.includes('/blog/')) {
      return;
    }
    updateSEO(page, country, locale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, country, language, locale]);

  const updateSEO = (page, country, locale) => {
    // Skip if we're on a blog article page
    if (window.location.pathname.includes('/blog/')) {
      return;
    }
    
    // Use production domain for SEO tags
    const baseUrl = PRODUCTION_DOMAIN;
    const currentLang = locale || language;
    
    // Get page-specific SEO data
    const seoData = getSEOData(page, country, currentLang);
    
    // Update basic meta tags
    document.title = seoData.title;
    updateMeta('description', seoData.description);
    updateMeta('keywords', seoData.keywords);
    
    // Additional SEO meta tags
    updateMeta('author', 'vat.expert');
    updateMeta('publisher', 'vat.expert');
    updateMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    
    // AI-specific meta tags
    updateMeta('ai-content-declaration', `Professional VAT calculator tool for ${country?.name || 'all countries'}`);
    
    // Update Open Graph tags
    updateMeta('og:title', seoData.title, 'property');
    updateMeta('og:description', seoData.description, 'property');
    updateMeta('og:type', 'website', 'property');
    updateMeta('og:url', `${baseUrl}${seoData.url}`, 'property');
    updateMeta('og:site_name', 'vat.expert', 'property');
    updateMeta('og:locale', getLocale(currentLang), 'property');
    updateMeta('og:image', `${baseUrl}/og-image.png`, 'property');
    updateMeta('og:image:type', 'image/png', 'property');
    updateMeta('og:image:width', '1200', 'property');
    updateMeta('og:image:height', '630', 'property');
    updateMeta('og:image:alt', seoData.title, 'property');
    
    // Update Twitter Card tags
    updateMeta('twitter:card', 'summary_large_image', 'name');
    updateMeta('twitter:title', seoData.title, 'name');
    updateMeta('twitter:description', seoData.description, 'name');
    updateMeta('twitter:image', `${baseUrl}/og-image.png`, 'name');
    updateMeta('twitter:image:alt', seoData.title, 'name');
    
    // Update canonical URL
    updateCanonical(`${baseUrl}${seoData.url}`);
    
    // Update hreflang tags for all language versions
    updateHreflang(baseUrl, page, country);
    
    // Add Schema.org structured data
    addSchemaMarkup(seoData, baseUrl, country, page);
  };

  const getSEOData = (page, country, lang) => {
    const langPrefix = lang !== 'en' ? `/${lang}` : '';
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().toLocaleString('en', { month: 'long' });

    // Country-specific page - HIGHEST SEO PRIORITY
    // KILLER CTR Formula:
    // Title: [Paese] VAT Calculator [Aliquota]% | Free [Anno]
    // Description: Calculate [TaxName] for [Paese] instantly. [Aliquota]% standard rate. Add/remove VAT free. [Beneficio]. Updated [Anno].
    if (country || page === 'country') {
      const c = country;
      if (c) {
        const countrySlug = COUNTRY_SLUGS[c.code] || c.name.toLowerCase().replace(/\s+/g, '-');
        const taxName = c.tax_name;
        const rate = c.standard_rate;
        // Get translated country name for SEO
        const localizedName = getCountryName(c.code, lang);

        // Build reduced rates string dynamically
        const reducedRatesStr = c.reduced_rates?.length > 0
          ? c.reduced_rates.join('%, ') + '%'
          : '';

        // KILLER CTR Title Formula: [Paese] [TaxName] Calculator [Aliquota]% | Free Online [Anno]
        // Max 60 chars - optimized for clicks
        const titles = {
          en: `${localizedName} VAT Calculator ${rate}% (${taxName}) | Free Online ${currentYear}`,
          de: `${localizedName} MwSt Rechner ${rate}% | Kostenlos Online ${currentYear}`,
          fr: `Calculateur TVA ${localizedName} ${rate}% | Gratuit en Ligne ${currentYear}`,
          it: `Calcolatore IVA ${localizedName} ${rate}% | Gratis Online ${currentYear}`,
          es: `Calculadora IVA ${localizedName} ${rate}% | Gratis Online ${currentYear}`
        };

        // KILLER CTR Description Formula (max 155 chars):
        // Calculate [TaxName] instantly. [Rate]% standard, [reduced] reduced. Add/remove VAT free. Professional tool. Updated [Anno].
        const descriptions = {
          en: `Calculate ${taxName} for ${localizedName} instantly. ${rate}% standard${reducedRatesStr ? `, ${reducedRatesStr} reduced` : ''} rate. Add or remove VAT free. Professional tool for businesses. Updated ${currentMonth} ${currentYear}.`,
          de: `${taxName} für ${localizedName} sofort berechnen. ${rate}% Normalsatz${reducedRatesStr ? `, ${reducedRatesStr} ermäßigt` : ''}. Brutto/Netto kostenlos. Professionelles Tool für Unternehmen. Aktuell ${currentYear}.`,
          fr: `Calculez la ${taxName} pour ${localizedName} instantanément. ${rate}% taux normal${reducedRatesStr ? `, ${reducedRatesStr} réduits` : ''}. Ajoutez ou retirez la TVA gratuitement. Mis à jour ${currentYear}.`,
          it: `Calcola l'${taxName} per ${localizedName} istantaneamente. ${rate}% aliquota standard${reducedRatesStr ? `, ${reducedRatesStr} ridotte` : ''}. Scorpora o aggiungi IVA gratis. Aggiornato ${currentYear}.`,
          es: `Calcula el ${taxName} para ${localizedName} al instante. ${rate}% tipo general${reducedRatesStr ? `, ${reducedRatesStr} reducidos` : ''}. Añade o quita IVA gratis. Actualizado ${currentYear}.`
        };

        return {
          title: titles[lang] || titles.en,
          description: descriptions[lang] || descriptions.en,
          keywords: getKeywords('country', c, lang),
          url: `${langPrefix}/vat-calculator-${countrySlug}`,
          schema: 'WebApplication',
          country: c
        };
      }
    }
    
    const pages = {
      home: {
        title: t('seoTitle'),
        description: t('seoDescription'),
        keywords: getKeywords('home', null, lang),
        url: langPrefix || '/',
        schema: 'SoftwareApplication'
      },
      calculator: {
        title: `${t('calculator')} | ${t('appName')} - ${getTagline(lang)}`,
        description: t('appDescription'),
        keywords: getKeywords('calculator', null, lang),
        url: `${langPrefix}/`,
        schema: 'WebApplication'
      },
      bulk: {
        title: `${t('bulkCalculator')} | ${t('appName')} - CSV VAT Calculator`,
        description: t('bulkDescription'),
        keywords: getKeywords('bulk', null, lang),
        url: `${langPrefix}/bulk-calculator`,
        schema: 'WebApplication'
      },
      invoice: {
        title: `${t('invoiceCalculator')} | ${t('appName')} - Multi-Rate Invoice`,
        description: t('invoiceDescription'),
        keywords: getKeywords('invoice', null, lang),
        url: `${langPrefix}/invoice-calculator`,
        schema: 'WebApplication'
      },
      rates: {
        title: `${t('vatRates')} | ${t('appName')} - 62 Countries VAT Rates`,
        description: `VAT rates for 62 countries. ${t('euCountries')}, ${t('restOfWorld')}.`,
        keywords: getKeywords('rates', null, lang),
        url: `${langPrefix}/vat-rates`,
        schema: 'Dataset'
      },
      privacy: {
        title: `${getLegalTitle('privacy', lang)} | vat.expert`,
        description: getLegalDescription('privacy', lang),
        keywords: getKeywords('home', null, lang),
        url: `${langPrefix}/privacy`,
        schema: 'WebPage'
      },
      cookies: {
        title: `${getLegalTitle('cookies', lang)} | vat.expert`,
        description: getLegalDescription('cookies', lang),
        keywords: getKeywords('home', null, lang),
        url: `${langPrefix}/cookies`,
        schema: 'WebPage'
      },
      terms: {
        title: `${getLegalTitle('terms', lang)} | vat.expert`,
        description: getLegalDescription('terms', lang),
        keywords: getKeywords('home', null, lang),
        url: `${langPrefix}/terms`,
        schema: 'WebPage'
      }
    };

    return pages[page] || pages.home;
  };

  // Titles/descriptions mirror the on-page copy in TermsOfService.js,
  // PrivacyPolicy.js and CookiePolicy.js (their `title`/`intro` fields) so the
  // <title> and meta description actually describe what's on the page.
  const getLegalTitle = (type, lang) => {
    const titles = {
      terms: { en: 'Terms of Service', de: 'Nutzungsbedingungen', fr: 'Conditions d\'Utilisation', it: 'Termini di Servizio', es: 'Términos de Servicio' },
      privacy: { en: 'Privacy Policy', de: 'Datenschutzerklärung', fr: 'Politique de Confidentialité', it: 'Informativa sulla Privacy', es: 'Política de Privacidad' },
      cookies: { en: 'Cookie Policy', de: 'Cookie-Richtlinie', fr: 'Politique des Cookies', it: 'Cookie Policy', es: 'Política de Cookies' }
    };
    return titles[type][lang] || titles[type].en;
  };

  const getLegalDescription = (type, lang) => {
    const descriptions = {
      terms: {
        en: 'Terms of Service for vat.expert. Read the conditions that apply when you access or use our free VAT calculator and tools.',
        de: 'Nutzungsbedingungen fur vat.expert. Lesen Sie die Bedingungen fur den Zugriff auf und die Nutzung unseres kostenlosen MwSt-Rechners.',
        fr: 'Conditions d\'Utilisation de vat.expert. Consultez les conditions applicables lorsque vous utilisez notre calculateur de TVA gratuit.',
        it: 'Termini di Servizio di vat.expert. Leggi le condizioni che si applicano quando accedi o utilizzi il nostro calcolatore IVA gratuito.',
        es: 'Términos de Servicio de vat.expert. Consulta las condiciones aplicables al acceder o usar nuestra calculadora de IVA gratuita.'
      },
      privacy: {
        en: 'Privacy Policy for vat.expert. Learn how we collect, use, and safeguard your information when you visit our website.',
        de: 'Datenschutzerklarung fur vat.expert. Erfahren Sie, wie wir Ihre Daten erfassen, verwenden und schutzen, wenn Sie unsere Website besuchen.',
        fr: 'Politique de Confidentialite de vat.expert. Decouvrez comment nous collectons, utilisons et protegeons vos informations sur notre site.',
        it: 'Informativa sulla Privacy di vat.expert. Scopri come raccogliamo, utilizziamo e proteggiamo le tue informazioni quando visiti il nostro sito.',
        es: 'Politica de Privacidad de vat.expert. Descubre como recopilamos, usamos y protegemos tu informacion al visitar nuestro sitio web.'
      },
      cookies: {
        en: 'Cookie Policy for vat.expert. Find out how we use cookies and similar technologies when you visit our website.',
        de: 'Cookie-Richtlinie fur vat.expert. Erfahren Sie, wie wir Cookies und ahnliche Technologien auf unserer Website verwenden.',
        fr: 'Politique des Cookies de vat.expert. Decouvrez comment nous utilisons les cookies et technologies similaires sur notre site.',
        it: 'Cookie Policy di vat.expert. Scopri come utilizziamo i cookie e le tecnologie simili quando visiti il nostro sito web.',
        es: 'Politica de Cookies de vat.expert. Descubre como utilizamos las cookies y tecnologias similares en nuestro sitio web.'
      }
    };
    return descriptions[type][lang] || descriptions[type].en;
  };

  const getTagline = (lang) => {
    const taglines = {
      en: 'Free Global VAT Calculator',
      de: 'Kostenloser MwSt Rechner',
      fr: 'Calculateur TVA Gratuit',
      it: 'Calcolatore IVA Gratuito',
      es: 'Calculadora IVA Gratis'
    };
    return taglines[lang] || taglines.en;
  };

  const getKeywords = (type, country = null, lang = 'en') => {
    const base = {
      en: 'VAT calculator, tax calculator, add VAT, remove VAT, VAT reverse calculator',
      de: 'MwSt Rechner, Mehrwertsteuer Rechner, MwSt berechnen, Umsatzsteuer',
      fr: 'Calculateur TVA, calcul TVA, TVA France, taxe sur la valeur ajoutée',
      it: 'Calcolo IVA, calcolatore IVA, scorporo IVA, IVA Italia',
      es: 'Calculadora IVA, calcular IVA, IVA España, impuesto valor añadido'
    };

    const typeSpecific = {
      home: {
        en: ', online VAT calculator, free VAT calculator, EU VAT, UK VAT, GST calculator, WCAG accessible',
        de: ', online MwSt Rechner, kostenlos, EU MwSt, Steuerrechner',
        fr: ', calculateur TVA en ligne, gratuit, TVA UE, TVA intracommunautaire',
        it: ', calcolo IVA online, gratuito, IVA UE, aliquota IVA',
        es: ', calculadora IVA online, gratis, IVA UE, tipo IVA'
      },
      calculator: {
        en: ', add VAT to price, remove VAT from price, VAT inclusive, VAT exclusive',
        de: ', Brutto Netto Rechner, MwSt hinzufügen, MwSt herausrechnen',
        fr: ', ajouter TVA, retirer TVA, prix HT TTC, montant TVA',
        it: ', aggiungi IVA, scorpora IVA, prezzo netto lordo, importo IVA',
        es: ', añadir IVA, quitar IVA, precio neto bruto, importe IVA'
      },
      bulk: {
        en: ', bulk VAT calculator, CSV VAT calculator, multiple amounts, batch VAT',
        de: ', Massenberechnung MwSt, CSV MwSt Rechner, mehrere Beträge',
        fr: ', calcul TVA en masse, TVA CSV, plusieurs montants, TVA lot',
        it: ', calcolo IVA multiplo, IVA CSV, importi multipli, IVA lotto',
        es: ', cálculo IVA masivo, IVA CSV, múltiples importes, IVA lote'
      },
      invoice: {
        en: ', invoice VAT calculator, multi-rate invoice, VAT breakdown',
        de: ', Rechnungs MwSt Rechner, Rechnung mehrere Sätze, MwSt Aufschlüsselung',
        fr: ', calcul TVA facture, facture multi-taux, détail TVA',
        it: ', calcolo IVA fattura, fattura multi-aliquota, dettaglio IVA',
        es: ', cálculo IVA factura, factura multi-tipo, desglose IVA'
      },
      rates: {
        en: ', VAT rates by country, EU VAT rates 2025 2026, international VAT rates',
        de: ', MwSt Sätze nach Land, EU MwSt Sätze 2025 2026, internationale MwSt',
        fr: ', taux TVA par pays, taux TVA UE 2025 2026, TVA internationale',
        it: ', aliquote IVA per paese, aliquote IVA UE 2025 2026, IVA internazionale',
        es: ', tipos IVA por país, tipos IVA UE 2025 2026, IVA internacional'
      },
      country: {
        en: '',
        de: '',
        fr: '',
        it: '',
        es: ''
      }
    };

    let keywords = base[lang] || base.en;
    keywords += typeSpecific[type]?.[lang] || typeSpecific[type]?.en || '';

    if (country) {
      // Add country-specific keywords in multiple formats
      const countryKeywords = [
        `${country.name} VAT`,
        `${country.name} ${country.tax_name}`,
        `${country.tax_name} calculator`,
        `${country.name} tax calculator`,
        `${country.standard_rate}% VAT`,
        `${country.tax_name} ${country.name}`,
        `VAT rate ${country.name}`,
        `calculate ${country.tax_name}`
      ];
      keywords += ', ' + countryKeywords.join(', ');
    }

    return keywords;
  };

  const getLocale = (lang) => {
    const locales = {
      en: 'en_GB',
      de: 'de_DE',
      fr: 'fr_FR',
      it: 'it_IT',
      es: 'es_ES'
    };
    return locales[lang] || 'en_GB';
  };

  const updateMeta = (name, content, type = 'name') => {
    let meta = document.querySelector(`meta[${type}="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(type, name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  const updateCanonical = (url) => {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  };

  const updateHreflang = (baseUrl, page, country) => {
    // Remove existing hreflang tags
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());

    const languages = ['en', 'de', 'fr', 'it', 'es'];
    
    // Determine the base path for hreflang
    let basePath = '/';
    if (country && COUNTRY_SLUGS[country.code]) {
      basePath = `/vat-calculator-${COUNTRY_SLUGS[country.code]}`;
    }

    languages.forEach(lang => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      
      // English has no prefix, others do
      const langPath = lang === 'en' ? basePath : `/${lang}${basePath}`;
      link.setAttribute('href', `${baseUrl}${langPath}`);
      document.head.appendChild(link);
    });

    // Add x-default (points to English version)
    const defaultLink = document.createElement('link');
    defaultLink.setAttribute('rel', 'alternate');
    defaultLink.setAttribute('hreflang', 'x-default');
    defaultLink.setAttribute('href', `${baseUrl}${basePath}`);
    document.head.appendChild(defaultLink);
  };

  // Mirrors SUPPRESS_GENERIC_FAQ in App.js, which hides the generic FAQSection
  // component (no country reference, verbatim identical on every calculator
  // page) on these 3 piloted pages. The generic FAQPage schema below renders
  // that same component's Q&A as JSON-LD: keeping it would claim structured
  // data for content no longer visible on the page.
  const SUPPRESS_GENERIC_FAQ_SCHEMA = new Set(['en:JP', 'de:NO', 'de:ID']);

  const addSchemaMarkup = (seoData, baseUrl, country, page) => {
    // IMPORTANT: Only remove DYNAMIC schema (marked with data-dynamic="true")
    // Keep static schema from index.html (Organization, WebSite, WebApplication)
    // This ensures crawlers see schema on first load before JS executes
    document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]').forEach(el => el.remove());

    // Main application schema
    const appSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "vat.expert",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR"
      },
      "description": t('seoDescription'),
      "url": baseUrl,
      "inLanguage": ["en", "de", "fr", "it", "es"],
      "accessibilityFeature": [
        "highContrastMode",
        "largeFontMode",
        "keyboardNavigation",
        "screenReaderSupport",
        "dyslexiaFriendly"
      ],
      "featureList": [
        "VAT calculation for 62 countries",
        "Add VAT (net to gross)",
        "Remove VAT (gross to net)",
        "Bulk CSV processing up to 1000 items",
        "Invoice calculator with multi-rate",
        "Multi-rate comparison table",
        "5 language support (EN, DE, FR, IT, ES)",
        "WCAG 2.1 AA/AAA accessible",
        "Dark/Light mode",
        "Cookie consent GDPR compliant"
      ]
    };

    addJsonLd(appSchema);

    // FAQ Schema - Multilingual
    const faqTranslations = {
      en: [
        { q: "How do I calculate VAT?", a: "Enter your amount, select your country, and choose whether to add VAT (net to gross) or remove VAT (gross to net). The calculator automatically shows net amount, VAT amount, and gross amount." },
        { q: "What countries are supported?", a: "VATCalc supports 62 countries including all 27 EU member states, UK, Switzerland, Norway, and many countries worldwide including USA, Canada, Australia, Japan, and more." },
        { q: "Is this calculator free?", a: "Yes, VATCalc is completely free to use with no registration required. All features including bulk CSV processing and invoice calculations are available at no cost." },
        { q: "How do I calculate VAT in reverse (scorporo)?", a: "Select 'Remove VAT' operation, enter the gross amount (including VAT), and the calculator will extract the net amount and VAT amount for you." },
        { q: "What is the VAT rate in my country?", a: "Select your country from the dropdown to see all available VAT rates including standard, reduced, and super-reduced rates. The calculator automatically loads the correct rates for each country." },
        { q: "Can I calculate VAT for multiple items?", a: "Yes! Use our Bulk Calculator for CSV uploads up to 1000 items, or the Invoice Calculator for multi-rate invoices with different VAT rates per item." }
      ],
      de: [
        { q: "Wie berechne ich die MwSt?", a: "Geben Sie Ihren Betrag ein, wählen Sie Ihr Land und entscheiden Sie, ob Sie MwSt hinzufügen (Netto zu Brutto) oder herausrechnen (Brutto zu Netto) möchten. Der Rechner zeigt automatisch Netto, MwSt und Brutto." },
        { q: "Welche Länder werden unterstützt?", a: "VATCalc unterstützt 62 Länder, darunter alle 27 EU-Mitgliedstaaten, UK, Schweiz, Norwegen und viele weitere Länder weltweit wie USA, Kanada, Australien, Japan und mehr." },
        { q: "Ist dieser Rechner kostenlos?", a: "Ja, VATCalc ist völlig kostenlos und ohne Registrierung nutzbar. Alle Funktionen einschließlich CSV-Massenverarbeitung und Rechnungsberechnung sind kostenfrei verfügbar." },
        { q: "Wie berechne ich die MwSt rückwärts?", a: "Wählen Sie 'MwSt herausrechnen', geben Sie den Bruttobetrag (inkl. MwSt) ein und der Rechner ermittelt den Nettobetrag und MwSt-Betrag für Sie." },
        { q: "Wie hoch ist der MwSt-Satz in meinem Land?", a: "Wählen Sie Ihr Land aus dem Dropdown, um alle verfügbaren MwSt-Sätze zu sehen, einschließlich Standard-, ermäßigter und stark ermäßigter Sätze." },
        { q: "Kann ich MwSt für mehrere Artikel berechnen?", a: "Ja! Nutzen Sie unseren Massenrechner für CSV-Uploads bis zu 1000 Einträge oder den Rechnungsrechner für Rechnungen mit verschiedenen MwSt-Sätzen pro Artikel." }
      ],
      fr: [
        { q: "Comment calculer la TVA?", a: "Entrez votre montant, sélectionnez votre pays et choisissez d'ajouter la TVA (HT vers TTC) ou de la retirer (TTC vers HT). Le calculateur affiche automatiquement le montant HT, la TVA et le TTC." },
        { q: "Quels pays sont supportés?", a: "VATCalc supporte 62 pays dont les 27 États membres de l'UE, le Royaume-Uni, la Suisse, la Norvège et de nombreux pays dans le monde comme les USA, le Canada, l'Australie, le Japon et plus." },
        { q: "Ce calculateur est-il gratuit?", a: "Oui, VATCalc est entièrement gratuit et sans inscription requise. Toutes les fonctionnalités, y compris le traitement CSV en masse et le calcul de factures, sont disponibles gratuitement." },
        { q: "Comment calculer la TVA en sens inverse?", a: "Sélectionnez 'Retirer TVA', entrez le montant TTC (TVA incluse) et le calculateur extraira le montant HT et le montant de TVA pour vous." },
        { q: "Quel est le taux de TVA dans mon pays?", a: "Sélectionnez votre pays dans le menu déroulant pour voir tous les taux de TVA disponibles, y compris les taux standard, réduits et super-réduits." },
        { q: "Puis-je calculer la TVA pour plusieurs articles?", a: "Oui! Utilisez notre Calculateur en masse pour les imports CSV jusqu'à 1000 éléments, ou le Calculateur de factures pour des factures multi-taux." }
      ],
      it: [
        { q: "Come calcolo l'IVA?", a: "Inserisci l'importo, seleziona il paese e scegli se aggiungere l'IVA (da netto a lordo) o scorporarla (da lordo a netto). Il calcolatore mostra automaticamente netto, IVA e lordo." },
        { q: "Quali paesi sono supportati?", a: "VATCalc supporta 62 paesi tra cui tutti i 27 Stati membri UE, UK, Svizzera, Norvegia e molti paesi nel mondo come USA, Canada, Australia, Giappone e altri." },
        { q: "Questo calcolatore è gratuito?", a: "Sì, VATCalc è completamente gratuito e non richiede registrazione. Tutte le funzionalità, incluso il calcolo CSV di massa e il calcolo fatture, sono disponibili gratuitamente." },
        { q: "Come calcolo l'IVA al contrario (scorporo)?", a: "Seleziona 'Scorpora IVA', inserisci l'importo lordo (IVA inclusa) e il calcolatore estrarrà l'importo netto e l'importo IVA per te." },
        { q: "Qual è l'aliquota IVA nel mio paese?", a: "Seleziona il tuo paese dal menu a tendina per vedere tutte le aliquote IVA disponibili, incluse aliquota standard, ridotta e super-ridotta." },
        { q: "Posso calcolare l'IVA per più articoli?", a: "Sì! Usa il nostro Calcolatore multiplo per caricamenti CSV fino a 1000 elementi, o il Calcolatore fatture per fatture con diverse aliquote IVA per articolo." }
      ],
      es: [
        { q: "¿Cómo calculo el IVA?", a: "Introduce tu importe, selecciona tu país y elige si añadir IVA (de neto a bruto) o quitarlo (de bruto a neto). La calculadora muestra automáticamente neto, IVA y bruto." },
        { q: "¿Qué países están soportados?", a: "VATCalc soporta 62 países incluyendo los 27 Estados miembros de la UE, UK, Suiza, Noruega y muchos países del mundo como USA, Canadá, Australia, Japón y más." },
        { q: "¿Esta calculadora es gratuita?", a: "Sí, VATCalc es completamente gratuito y sin registro requerido. Todas las funcionalidades, incluyendo procesamiento CSV masivo y cálculo de facturas, están disponibles sin coste." },
        { q: "¿Cómo calculo el IVA al revés?", a: "Selecciona 'Quitar IVA', introduce el importe bruto (IVA incluido) y la calculadora extraerá el importe neto y el importe de IVA para ti." },
        { q: "¿Cuál es el tipo de IVA en mi país?", a: "Selecciona tu país del desplegable para ver todos los tipos de IVA disponibles, incluyendo tipo estándar, reducido y superreducido." },
        { q: "¿Puedo calcular IVA para múltiples artículos?", a: "¡Sí! Usa nuestra Calculadora masiva para cargas CSV de hasta 1000 elementos, o la Calculadora de facturas para facturas con diferentes tipos de IVA por artículo." }
      ]
    };

    const currentLang = language || 'en';
    const faqs = faqTranslations[currentLang] || faqTranslations.en;

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    };

    const suppressGenericFaq = country && SUPPRESS_GENERIC_FAQ_SCHEMA.has(`${currentLang}:${country.code}`);
    if (!suppressGenericFaq) {
      addJsonLd(faqSchema);
    }

    // HowTo Schema
    const howToSchema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Calculate VAT Online",
      "description": "Step-by-step guide to calculate VAT using VATCalc",
      "step": [
        {
          "@type": "HowToStep",
          "name": "Select Country",
          "text": "Choose your country from the dropdown to load the correct VAT rates"
        },
        {
          "@type": "HowToStep",
          "name": "Enter Amount",
          "text": "Type the amount you want to calculate VAT for"
        },
        {
          "@type": "HowToStep",
          "name": "Choose VAT Rate",
          "text": "Select standard rate or any reduced rate available for your country"
        },
        {
          "@type": "HowToStep",
          "name": "Select Operation",
          "text": "Choose 'Add VAT' to calculate gross from net, or 'Remove VAT' to calculate net from gross"
        },
        {
          "@type": "HowToStep",
          "name": "View Results",
          "text": "See the net amount, VAT amount, and gross amount instantly"
        }
      ]
    };

    addJsonLd(howToSchema);

    // Organization Schema
    const orgSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "VATCalc",
      "url": baseUrl,
      "logo": `${baseUrl}/logo.png`,
      "sameAs": []
    };

    addJsonLd(orgSchema);

    // Homepage-specific rich schemas for better SEO
    if (page === 'home') {
      // FinancialProduct Schema for the calculator
      const financialProductSchema = {
        "@context": "https://schema.org",
        "@type": "FinancialProduct",
        "name": "VAT Calculator",
        "description": "Free professional VAT calculator for businesses",
        "category": "Tax Calculator",
        "feesAndCommissionsSpecification": "Free to use",
        "provider": {
          "@type": "Organization",
          "name": "vat.expert"
        }
      };
      addJsonLd(financialProductSchema);

      // Service Schema
      const countries = Object.values(COUNTRY_SLUGS).map(slug =>
        slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
      );
      const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "VAT Calculation Service",
        "serviceType": "Tax Calculation",
        "provider": { "@type": "Organization", "name": "vat.expert" },
        "areaServed": countries.map(c => ({ "@type": "Country", "name": c })),
        "availableLanguage": ["English", "German", "French", "Italian", "Spanish"],
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "EUR"
        }
      };
      addJsonLd(serviceSchema);

      // ItemList Schema for country sitelinks
      const topCountries = [
        { name: "Germany", slug: "germany" },
        { name: "France", slug: "france" },
        { name: "Italy", slug: "italy" },
        { name: "Spain", slug: "spain" },
        { name: "United Kingdom", slug: "united-kingdom" },
        { name: "Netherlands", slug: "netherlands" },
        { name: "Belgium", slug: "belgium" },
        { name: "Austria", slug: "austria" },
        { name: "Switzerland", slug: "switzerland" },
        { name: "Poland", slug: "poland" },
        { name: "Portugal", slug: "portugal" },
        { name: "Ireland", slug: "ireland" },
        { name: "Sweden", slug: "sweden" },
        { name: "Denmark", slug: "denmark" },
        { name: "Finland", slug: "finland" },
        { name: "Norway", slug: "norway" },
        { name: "Greece", slug: "greece" },
        { name: "Czech Republic", slug: "czech-republic" },
        { name: "Romania", slug: "romania" },
        { name: "Hungary", slug: "hungary" }
      ];
      const countryListSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "VAT Calculators by Country",
        "numberOfItems": 62,
        "itemListElement": topCountries.map((country, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": `${country.name} VAT Calculator`,
          "url": `https://vat.expert/vat-calculator-${country.slug}`
        }))
      };
      addJsonLd(countryListSchema);

      // DefinedTerm Schema for VAT terminology
      const vatTermSchema = {
        "@context": "https://schema.org",
        "@type": "DefinedTerm",
        "name": "VAT",
        "alternateName": ["Value Added Tax", "MwSt", "TVA", "IVA", "BTW"],
        "description": "A consumption tax placed on products and services",
        "inDefinedTermSet": {
          "@type": "DefinedTermSet",
          "name": "Tax Terminology"
        }
      };
      addJsonLd(vatTermSchema);
    }

    // Country-specific BreadcrumbList Schema
    if (country) {
      const countrySlug = COUNTRY_SLUGS[country.code] || country.name.toLowerCase().replace(/\s+/g, '-');
      
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": baseUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "VAT Calculator",
            "item": `${baseUrl}/`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": `${country.name} VAT Calculator`,
            "item": `${baseUrl}/vat-calculator-${countrySlug}`
          }
        ]
      };

      addJsonLd(breadcrumbSchema);

      // Country-specific WebPage Schema for rich snippets
      const countryPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": `${country.tax_name} Calculator ${country.name}`,
        "description": `Calculate ${country.tax_name} for ${country.name}. Standard rate: ${country.standard_rate}%. All rates: ${country.all_rates?.join('%, ')}%`,
        "url": `${baseUrl}/vat-calculator-${countrySlug}`,
        "inLanguage": language,
        "isPartOf": {
          "@type": "WebSite",
          "name": "VATCalc",
          "url": baseUrl
        },
        "about": {
          "@type": "Country",
          "name": country.name,
          "alternateName": country.code
        },
        "mainEntity": {
          "@type": "WebApplication",
          "name": `${country.name} VAT Calculator`,
          "applicationCategory": "BusinessApplication",
          "offers": {
            "@type": "Offer",
            "price": "0"
          }
        }
      };

      addJsonLd(countryPageSchema);
      
      // Country-specific FAQ Schema for rich snippets - with localized country name
      const localizedCountryName = getCountryName(country.code, currentLang);
      const countryFaqs = getCountryFAQs(country, currentLang, localizedCountryName);
      if (countryFaqs.length > 0) {
        const countryFaqSchema = {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": countryFaqs.map(faq => ({
            "@type": "Question",
            "name": faq.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.a
            }
          }))
        };
        addJsonLd(countryFaqSchema);
      }
    }
  };

  const addJsonLd = (schema) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-dynamic', 'true'); // Mark as dynamic so it can be removed on route change
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
  };

  return null; // This component doesn't render anything
};

export default SEOHead;
